import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FiSearch, FiCheck, FiPlusSquare, FiMinusSquare } from 'react-icons/fi';

// =============================================
// Collapsible, multi-select owner tree.
//   Account Owner [+]  →  Family [+] → members,  Businesses [+] → businesses
//   Client        [+]  →  (same shape)
// Selectable nodes (ownerKey set) get a checkbox; group nodes only expand.
// Keyboard (focus in the search box): type to filter · ↑/↓ move · →/← expand/collapse
//   · Enter toggles the highlighted node (select if selectable, else expand) · Tab leaves.
// =============================================

export interface TreeNode {
  id: string;
  label: string;
  sub?: string;
  ownerKey?: string;          // present → selectable
  children?: TreeNode[];
}

interface Props {
  nodes: TreeNode[];
  selected: string[];
  onToggle: (key: string) => void;
  placeholder?: string;
  emptyText?: string;
  maxHeight?: number;
}

interface Row { node: TreeNode; depth: number; hasChildren: boolean; expanded: boolean; }

function flatten(nodes: TreeNode[], depth: number, expanded: Set<string>, out: Row[]) {
  for (const n of nodes) {
    const hasChildren = !!(n.children && n.children.length);
    const isOpen = expanded.has(n.id);
    out.push({ node: n, depth, hasChildren, expanded: isOpen });
    if (hasChildren && isOpen) flatten(n.children!, depth + 1, expanded, out);
  }
}

// Filter the tree to nodes matching `q` (or having a matching descendant).
function filterTree(nodes: TreeNode[], q: string): { nodes: TreeNode[]; expand: Set<string> } {
  const expand = new Set<string>();
  const walk = (list: TreeNode[]): TreeNode[] => {
    const out: TreeNode[] = [];
    for (const n of list) {
      const kids = n.children ? walk(n.children) : [];
      const selfMatch = n.label.toLowerCase().includes(q);
      if (selfMatch || kids.length) {
        out.push({ ...n, children: kids.length ? kids : n.children });
        if (kids.length) expand.add(n.id);
      }
    }
    return out;
  };
  return { nodes: walk(nodes), expand };
}

const OwnerTree: React.FC<Props> = ({ nodes, selected, onToggle, placeholder = 'Search owners…', emptyText = 'No owners found.', maxHeight = 260 }) => {
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [hi, setHi] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const searching = q.trim().length > 0;
  const view = useMemo(() => {
    if (!searching) return { nodes, expand: expanded };
    return filterTree(nodes, q.trim().toLowerCase());
  }, [nodes, q, searching, expanded]);

  const rows = useMemo(() => {
    const out: Row[] = [];
    flatten(view.nodes, 0, view.expand, out);
    return out;
  }, [view]);

  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${hi}"]`) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [hi, rows.length]);

  const toggleExpand = (id: string) =>
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const activate = (r: Row) => {
    if (r.node.ownerKey) onToggle(r.node.ownerKey);
    else if (r.hasChildren) toggleExpand(r.node.id);
  };

  const onKey = (e: React.KeyboardEvent) => {
    const r = rows[hi];
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(rows.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
    else if (e.key === 'PageDown') { e.preventDefault(); setHi(h => Math.min(rows.length - 1, h + 10)); }
    else if (e.key === 'PageUp') { e.preventDefault(); setHi(h => Math.max(0, h - 10)); }
    else if (e.key === 'Home') { e.preventDefault(); setHi(0); }
    else if (e.key === 'End') { e.preventDefault(); setHi(rows.length - 1); }
    else if (e.key === 'ArrowRight') { if (r && r.hasChildren && !r.expanded && !searching) { e.preventDefault(); toggleExpand(r.node.id); } }
    else if (e.key === 'ArrowLeft') { if (r && r.hasChildren && r.expanded && !searching) { e.preventDefault(); toggleExpand(r.node.id); } }
    else if (e.key === 'Enter') { e.preventDefault(); if (r) activate(r); }
    // Tab: default (leaves the field)
  };

  return (
    <div className="otr">
      <div className="otr-search">
        <FiSearch size={13} />
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} placeholder={placeholder}
          aria-label="Owners — type to search, arrows to move, right/left to expand/collapse, Enter to select, Tab to leave" />
      </div>
      <div className="otr-list" ref={listRef} style={{ maxHeight }}>
        {rows.length === 0 ? (
          <div className="otr-empty">{emptyText}</div>
        ) : rows.map((r, i) => {
          const on = !!r.node.ownerKey && selected.includes(r.node.ownerKey);
          return (
            <div
              key={r.node.id}
              data-idx={i}
              className={`otr-row ${i === hi ? 'hi' : ''} ${on ? 'on' : ''} ${r.node.ownerKey ? 'sel' : 'grp'}`}
              style={{ paddingLeft: 6 + r.depth * 16 }}
              onMouseEnter={() => setHi(i)}
              onClick={() => activate(r)}
            >
              <span className="otr-twisty" onClick={(e) => { if (r.hasChildren) { e.stopPropagation(); toggleExpand(r.node.id); } }}>
                {r.hasChildren ? (r.expanded || searching ? <FiMinusSquare size={13} /> : <FiPlusSquare size={13} />) : null}
              </span>
              {r.node.ownerKey && <span className="otr-check">{on && <FiCheck size={13} />}</span>}
              <span className="otr-label">
                {r.node.label}{r.node.sub && <span className="otr-sub"> · {r.node.sub}</span>}
              </span>
            </div>
          );
        })}
      </div>
      {selected.length > 0 && <div className="otr-count">{selected.length} selected</div>}
    </div>
  );
};

export default OwnerTree;
